import { ComponentProps, ReactNode, forwardRef } from 'react';
import styled from 'styled-components';
import { getVariantColors } from './buttonUtils';
import { ButtonVariants } from './ButtonVariants';

interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariants;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

const StyledButton = styled.button`
  padding: 0.5rem 1rem;
  gap: 0.5rem;
  height: 3rem;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 1rem;
  line-height: 100%;
  letter-spacing: 0.02rem;
  border-radius: 2.5rem;
  background-color: ${({ variant }: ButtonProps) =>
    getVariantColors(variant).backgroundColor};
  color: ${({ variant }: ButtonProps) => getVariantColors(variant).color};
  border: ${({ variant }: ButtonProps) => getVariantColors(variant).border};

  &:hover {
    background-color: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).hoverBackgroundColor};
    color: ${({ variant }: ButtonProps) => getVariantColors(variant).color};
    border: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).hoverBorder};
  }

  &:focus-visible {
    outline: black solid 2px;
    outline-offset: 2px;
  }

  &:disabled {
    color: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).disabledColor};
    cursor: default;
    background-color: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).disabledBackgroundColor};
    border: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).disabledBorder};
    pointer-events: none;
  }

  &:active {
    background-color: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).activeBackgroundColor};
    border: ${({ variant }: ButtonProps) =>
      getVariantColors(variant).activeBorder};
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      className,
      children,
      startIcon,
      endIcon,
      type = 'button',
      ...rest
    } = props;

    return (
      <StyledButton {...rest} className={className} type={type} ref={ref}>
        {startIcon}
        {children}
        {endIcon}
      </StyledButton>
    );
  }
);

export default Button;
